import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import './ResetPassword.css';

function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState(null);

  useEffect(() => {
    // Check if user has a valid session (Supabase auto-creates session when clicking reset link)
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        // User has a session from the reset link, we can proceed
        setToken('session-exists');
        return;
      }
      
      // Try to get token from URL hash as fallback
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');
      
      if (accessToken && type === 'recovery') {
        setToken(accessToken);
      } else {
        setError('Invalid or missing reset token. Please request a new password reset link.');
      }
    };
    
    checkSession();
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      // Check if we have an active session (Supabase auto-creates it when clicking reset link)
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // If no session, try to set it from token (fallback)
        if (token && token !== 'session-exists') {
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: token,
            refresh_token: ''
          });
          
          if (setSessionError) throw setSessionError;
        } else {
          throw new Error('No active session. Please click the reset link from your email again.');
        }
      }

      // Update password using the existing or newly set session
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      setSuccess(true);
      
      // Wait a moment then redirect to home
      // The session will persist (user stays logged in) as requested
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token && !error) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !token) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <h1>Reset Password</h1>
          <div className="error-message">{error}</div>
          <a href="/signin" className="back-link">← Back to Sign In</a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <h1>Password Reset Successful!</h1>
          <div className="success-message">
            Your password has been updated. Redirecting you...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <h1>Reset Password</h1>
        <p>Please enter your new password below.</p>

        <form onSubmit={handleResetPassword} className="reset-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              required
              minLength={6}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength={6}
              disabled={loading}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="reset-button" disabled={loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <a href="/signin" className="back-link">← Back to Sign In</a>
      </div>
    </div>
  );
}

export default ResetPassword;

